import { useState, useEffect } from "react";
import { CONFIG, goToCheckout } from "../../config";
import { ShieldCheck, Menu, X } from "lucide-react";
import { useI18n } from "../../i18n";
import LanguageSwitcher from "./LanguageSwitcher";
import CurrencySwitcher from "./CurrencySwitcher";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, currency } = useI18n();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { key: "nav_features", href: "#features" },
    { key: "nav_dashboard", href: "#dashboard" },
    { key: "nav_comparison", href: "#comparison" },
    { key: "nav_pricing", href: "#pricing" },
    { key: "nav_faq", href: "#faq" },
  ];

  return (
    <header data-testid="site-nav"
      className={`sticky top-0 inset-x-0 z-50 transition-all ${
        scrolled ? "backdrop-blur-xl bg-[#0a0a0b]/85 border-b border-[#23232a]" : "bg-[#0a0a0b]/40 backdrop-blur-md border-b border-[#23232a]/50"
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
          <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg grid place-items-center"
            style={{ background: "linear-gradient(135deg,#f5d06a,#8a6a15)", boxShadow: "0 0 24px rgba(231,180,59,.35)" }}>
            <ShieldCheck className="w-4 h-4 text-[#141006]" />
          </span>
          <div className="leading-tight hidden xs:block">
            <div className="font-display font-bold text-[14px] sm:text-[15px]">Prop Passer Gold</div>
            <div className="font-mono text-[9px] sm:text-[10px] text-[color:var(--ink-mute)] uppercase tracking-widest">XAUUSD · MT5</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-6 flex-1 justify-center">
          {links.map((l) => (
            <a key={l.href} href={l.href}
              className="text-sm text-[color:var(--ink-dim)] hover:text-[color:var(--gold)] transition-colors">
              {t(l.key)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <LanguageSwitcher />
          <CurrencySwitcher />
          <button onClick={() => goToCheckout(currency.code, currency.price)} data-testid="nav-cta-buy"
            className="hidden sm:inline-flex btn-gold px-4 h-10 rounded-full items-center text-sm font-display whitespace-nowrap">
            {t("nav_get_access")} · {currency.symbol}{currency.price.toLocaleString(currency.code === "INR" ? "en-IN" : "en-US")}
          </button>
          <button data-testid="nav-menu-toggle"
            className="lg:hidden w-10 h-10 grid place-items-center rounded-full border border-[#23232a]"
            onClick={() => setOpen((v) => !v)} aria-label="Menu">
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#23232a] bg-[#0a0a0b]/95 backdrop-blur-xl">
          <div className="px-5 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)}
                className="text-sm text-[color:var(--ink-dim)] hover:text-[color:var(--gold)]">
                {t(l.key)}
              </a>
            ))}
            <button onClick={() => { setOpen(false); goToCheckout(currency.code, currency.price); }}
              className="btn-gold px-4 h-11 rounded-full grid place-items-center text-sm font-display">
              {t("nav_get_access")} · {currency.symbol}{currency.price.toLocaleString(currency.code === "INR" ? "en-IN" : "en-US")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
